import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  ChevronRight, Clock, ArrowLeft, Share2, CalendarDays,
  Eye, Check, Loader2, Newspaper,
} from 'lucide-react';
import MarkdownContent from '@/components/blog/MarkdownContent';
import BlogCard, { AuthorLine, formatBlogDate } from '@/components/blog/BlogCard';

const API_BASE = 'https://xceededge.com';

export default function BlogDetail() {
  // The route param is the post's slug (/blog/:slug); older links that used a
  // numeric id still resolve because the endpoint accepts either.
  const { slug } = useParams();
  const navigate = useNavigate();

  const [post, setPost]       = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    window.scrollTo(0, 0);

    const params = /^\d+$/.test(String(slug)) ? { id: slug } : { slug };

    axios
      .get(`${API_BASE}/get-blogs.php`, { params: { action: 'detail', ...params } })
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success) {
          setPost(res.data.data);
          setRelated(res.data.related || []);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: post?.title, text: post?.excerpt || '', url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* user dismissed the share sheet, or the clipboard was blocked — nothing to do */
    }
  };

  if (loading) return <ArticleSkeleton />;

  if (notFound || !post) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center gap-3 px-4 pt-40 pb-24 text-center">
        <Newspaper className="h-9 w-9 text-muted-foreground/50" />
        <h1 className="font-heading text-2xl font-bold">Article not found</h1>
        <p className="text-sm text-muted-foreground">
          This post may have been unpublished or its link has changed.
        </p>
        <Link
          to="/blog"
          className="mt-2 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:brightness-95"
        >
          <ArrowLeft className="h-4 w-4" /> Back to the blog
        </Link>
      </div>
    );
  }

  const tags = Array.isArray(post.tags) ? post.tags : [];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pb-20">
      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="border-b bg-gradient-to-b from-primary/5 to-transparent pt-28 pb-10">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <nav className="mb-6 flex items-center gap-2 text-sm text-muted-foreground" aria-label="Breadcrumb">
            <Link to="/" className="transition-colors hover:text-primary">Home</Link>
            <ChevronRight className="h-3 w-3 shrink-0" />
            <Link to="/blog" className="transition-colors hover:text-primary">Blog</Link>
            <ChevronRight className="h-3 w-3 shrink-0" />
            <span className="truncate text-foreground">{post.title}</span>
          </nav>

          {post.category && (
            <span className="inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              {post.category}
            </span>
          )}

          <h1 className="mt-3 font-heading text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
            {post.title}
          </h1>

          {post.excerpt && (
            <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              {post.excerpt}
            </p>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t pt-5">
            <AuthorLine post={post} />
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              {post.published_at && (
                <span className="flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5" /> {formatBlogDate(post.published_at)}
                </span>
              )}
              {post.read_time && (
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" /> {post.read_time}
                </span>
              )}
              {post.views > 0 && (
                <span className="flex items-center gap-1.5">
                  <Eye className="h-3.5 w-3.5" /> {post.views}
                </span>
              )}
              <button
                onClick={share}
                className="flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-medium transition hover:bg-muted"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Share2 className="h-3.5 w-3.5" />}
                {copied ? 'Link copied' : 'Share'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-4 sm:px-6">
        {post.cover_image && (
          <img
            src={post.cover_image}
            alt={post.title}
            className="mt-8 aspect-[16/9] w-full rounded-2xl border object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}

        <div className="mt-8">
          <MarkdownContent content={post.content} />
        </div>

        {tags.length > 0 && (
          <div className="mt-10 flex flex-wrap items-center gap-2 border-t pt-6">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tags</span>
            {tags.map((tag) => (
              <span key={tag} className="rounded-full border px-2.5 py-1 text-xs text-muted-foreground">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Author card */}
        {post.author_name && (
          <div className="mt-8 flex items-center gap-4 rounded-2xl border bg-card p-5">
            {post.author_image ? (
              <img src={post.author_image} alt={post.author_name} className="h-14 w-14 rounded-full border object-cover" />
            ) : (
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-lg font-bold text-primary">
                {post.author_name.trim().charAt(0).toUpperCase()}
              </span>
            )}
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Written by</p>
              <p className="font-heading font-bold text-foreground">{post.author_name}</p>
              {post.author_role && <p className="text-sm text-muted-foreground">{post.author_role}</p>}
            </div>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition hover:bg-muted"
          >
            <ArrowLeft className="h-4 w-4" /> All articles
          </button>
          <button
            onClick={share}
            className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition hover:bg-muted"
          >
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Share2 className="h-4 w-4" />}
            {copied ? 'Link copied' : 'Share'}
          </button>
        </div>
      </article>

      {/* ── Related ───────────────────────────────────────────── */}
      {related.length > 0 && (
        <section className="mx-auto mt-16 max-w-6xl px-4 sm:px-6">
          <div className="mb-5 flex items-baseline justify-between gap-3 border-t pt-10">
            <h2 className="font-heading text-xl font-bold">Keep reading</h2>
            <Link to="/blog" className="text-sm font-semibold text-primary hover:underline">
              View all
            </Link>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((p, i) => <BlogCard key={p.id} post={p} index={i} />)}
          </div>
        </section>
      )}
    </motion.div>
  );
}

function ArticleSkeleton() {
  return (
    <div className="mx-auto max-w-3xl animate-pulse px-4 pt-32 pb-20 sm:px-6">
      <div className="h-3 w-40 rounded bg-muted" />
      <div className="mt-6 h-5 w-24 rounded-full bg-muted" />
      <div className="mt-4 h-9 w-full rounded bg-muted" />
      <div className="mt-2 h-9 w-4/5 rounded bg-muted" />
      <div className="mt-6 h-4 w-full rounded bg-muted" />
      <div className="mt-2 h-4 w-2/3 rounded bg-muted" />
      <div className="mt-8 aspect-[16/9] w-full rounded-2xl bg-muted" />
      <div className="mt-8 space-y-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className={`h-4 rounded bg-muted ${i % 4 === 3 ? 'w-3/5' : 'w-full'}`} />
        ))}
      </div>
      <div className="mt-10 flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading article…
      </div>
    </div>
  );
}
